'use client';

import { useState } from 'react';
import { Zap, Loader2, CheckCircle2, ArrowRight } from 'lucide-react';
import type { InstantQuoteProduct } from '@/lib/pax8/catalog';

/**
 * Instant-quote form. Renders whatever fields the product declares in
 * lib/pax8/catalog.ts, so supporting a new product is a catalog entry rather
 * than a new component.
 *
 * On success the price is shown inline as well as emailed — someone who just
 * typed their details should not have to go to their inbox to see the number
 * they asked for.
 */

interface InstantQuoteFormProps {
  product: InstantQuoteProduct;
}

interface QuoteResult {
  productName: string;
  variantLabel?: string;
  lineItems?: Array<{
    label: string;
    kind?: 'recurring' | 'one-time';
    unitPrice: number;
    total: number;
  }>;
  seats: number;
  billingTerm?: string;
  commitmentTerm?: string;
  unitPrice: number;
  /** Recurring total, per billing period. Never includes oneTimeTotal. */
  total: number;
  /** Setup charges, billed once. Rendered as its own total — see below. */
  oneTimeTotal?: number;
  currency: string;
}

const money = (n: number, currency: string) =>
  `${currency === 'USD' ? '$' : ''}${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function InstantQuoteForm({ product }: InstantQuoteFormProps) {
  const [answers, setAnswers] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    for (const f of product.fields) if (f.defaultValue) seed[f.name] = f.defaultValue;
    return seed;
  });
  const [contact, setContact] = useState({ fullName: '', email: '', company: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quote, setQuote] = useState<QuoteResult | null>(null);
  const [pending, setPending] = useState(false);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);

  const setAnswer = (name: string, value: string) =>
    setAnswers((prev) => ({ ...prev, [name]: value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const res = await fetch('/api/software-quote/instant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...contact,
          productSlug: product.storeSlug,
          answers,
          sourcePage:
            typeof window !== 'undefined'
              ? window.location.pathname + window.location.search
              : undefined,
        }),
      });
      const body = await res.json();

      if (!res.ok) {
        setError(body.error || 'Something went wrong. Please try again.');
        return;
      }

      if (body.priced && body.quote) {
        setQuote(body.quote);
        setExpiresAt(body.expiresAt ?? null);
      } else {
        // Priced automatically is the fast path, not the only path. When Pax8
        // cannot price it we still have the lead, and saying so plainly beats
        // showing an error for a request that actually succeeded.
        setPending(true);
      }
    } catch {
      setError('Could not reach the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (quote) {
    // Two horizons. The recurring block and the setup block each carry their
    // own total and are never added together — a subscription total and a
    // one-off setup fee summed into a single headline is a number that stops
    // being true after the first billing period.
    const recurringLines = (quote.lineItems ?? []).filter((l) => l.kind !== 'one-time');
    const oneTimeLines = (quote.lineItems ?? []).filter((l) => l.kind === 'one-time');
    return (
      <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 p-6">
        <div className="flex items-center gap-2 mb-4 text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-semibold">Your quote is ready</span>
        </div>
        {recurringLines.length > 1 ? (
          <dl className="space-y-2 text-sm mb-3">
            {recurringLines.map((l) => (
              <div key={l.label} className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">{l.label}</dt>
                <dd className="text-gray-900 dark:text-gray-100">
                  {money(l.total, quote.currency)}
                </dd>
              </div>
            ))}
          </dl>
        ) : null}
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between border-b border-emerald-200/60 dark:border-emerald-800/60 pb-2">
            <dt className="text-gray-600 dark:text-gray-400">Quantity</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {quote.seats.toLocaleString('en-US')} {product.unitLabel}
            </dd>
          </div>
          <div className="flex justify-between border-b border-emerald-200/60 dark:border-emerald-800/60 pb-2">
            <dt className="text-gray-600 dark:text-gray-400">Per unit</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {money(quote.unitPrice, quote.currency)}
            </dd>
          </div>
          <div className="flex justify-between border-b border-emerald-200/60 dark:border-emerald-800/60 pb-2">
            <dt className="text-gray-600 dark:text-gray-400">Billing</dt>
            <dd className="font-medium text-gray-900 dark:text-gray-100">
              {quote.billingTerm ?? '—'}
            </dd>
          </div>
          <div className="flex justify-between pt-1">
            <dt className="text-base font-bold text-gray-900 dark:text-gray-100">
              {quote.oneTimeTotal ? 'Subscription total' : 'Total'}
            </dt>
            <dd className="text-base font-bold text-gray-900 dark:text-gray-100">
              {money(quote.total, quote.currency)}
              {quote.oneTimeTotal ? (
                <span className="font-normal text-gray-500 dark:text-gray-400">
                  {' '}
                  / {(quote.billingTerm ?? '').toLowerCase() === 'annual' ? 'year' : 'month'}
                </span>
              ) : null}
            </dd>
          </div>
        </dl>

        {quote.oneTimeTotal ? (
          <dl className="mt-4 pt-4 border-t border-emerald-200/60 dark:border-emerald-800/60 space-y-2 text-sm">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              One-time setup — charged once, separately from the subscription above.
            </p>
            {oneTimeLines.map((l) => (
              <div key={l.label} className="flex justify-between">
                <dt className="text-gray-600 dark:text-gray-400">{l.label}</dt>
                <dd className="text-gray-900 dark:text-gray-100">
                  {money(l.total, quote.currency)}
                </dd>
              </div>
            ))}
            <div className="flex justify-between pt-1">
              <dt className="text-base font-bold text-gray-900 dark:text-gray-100">
                One-time setup total
              </dt>
              <dd className="text-base font-bold text-gray-900 dark:text-gray-100">
                {money(quote.oneTimeTotal, quote.currency)}
              </dd>
            </div>
          </dl>
        ) : null}

        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          We have emailed this to <strong>{contact.email}</strong>
          {expiresAt ? ` — it is valid until ${new Date(expiresAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.` : '.'}
        </p>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-500">
          Based on current distribution rates. Indicative rather than a contract —
          final pricing is confirmed when the order is placed.
        </p>
      </div>
    );
  }

  if (pending) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6">
        <div className="flex items-center gap-2 mb-2 text-gray-900 dark:text-gray-100">
          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          <span className="font-semibold">Request received</span>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          We could not price this one automatically, so a person is putting your
          quote together. You will hear back at <strong>{contact.email}</strong>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
        <Zap className="w-4 h-4" />
        Instant quote
      </div>

      {product.fields.map((field) => (
        <div key={field.name}>
          <label
            htmlFor={`iq-${field.name}`}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {field.label}
            {field.required && <span className="text-red-500"> *</span>}
          </label>

          {field.type === 'multiselect' ? (
            <div className="space-y-2">
              {field.options?.map((o) => {
                const selected = (answers[field.name] ?? '').split(',').filter(Boolean);
                const checked = selected.includes(o.value);
                return (
                  <label key={o.value} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setAnswer(
                          field.name,
                          (checked
                            ? selected.filter((v) => v !== o.value)
                            : [...selected, o.value]
                          ).join(',')
                        )
                      }
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    {o.label}
                  </label>
                );
              })}
            </div>
          ) : field.type === 'select' ? (
            <select
              id={`iq-${field.name}`}
              name={field.name}
              required={field.required}
              value={answers[field.name] ?? ''}
              onChange={(e) => setAnswer(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="">Select…</option>
              {field.options?.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={`iq-${field.name}`}
              name={field.name}
              type={field.type === 'number' ? 'number' : 'text'}
              inputMode={field.type === 'number' ? 'numeric' : undefined}
              min={field.min}
              max={field.max}
              required={field.required}
              placeholder={field.placeholder}
              value={answers[field.name] ?? ''}
              onChange={(e) => setAnswer(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          )}

          {field.help && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{field.help}</p>
          )}
        </div>
      ))}

      <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="sm:col-span-2">
          <label htmlFor="iq-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Your name <span className="text-red-500">*</span>
          </label>
          <input
            id="iq-name"
            required
            value={contact.fullName}
            onChange={(e) => setContact({ ...contact, fullName: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label htmlFor="iq-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Work email <span className="text-red-500">*</span>
          </label>
          <input
            id="iq-email"
            type="email"
            required
            value={contact.email}
            onChange={(e) => setContact({ ...contact, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div>
          <label htmlFor="iq-company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Company <span className="text-red-500">*</span>
          </label>
          <input
            id="iq-company"
            required
            value={contact.company}
            onChange={(e) => setContact({ ...contact, company: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="iq-phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phone <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="iq-phone"
            type="tel"
            value={contact.phone}
            onChange={(e) => setContact({ ...contact, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#2813e8] to-[#2cc3d1] text-white px-5 py-3 rounded-md font-semibold hover:shadow-lg transition-all disabled:opacity-60"
      >
        {submitting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Pricing your quote…
          </>
        ) : (
          <>
            Get my price
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        No obligation. We will email the quote so you have it in writing.
      </p>
    </form>
  );
}
